// src/components/ProcessingResults/RotatedStage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import { Spinner, Alert } from 'react-bootstrap';
import DraggableCell from './DraggableCell';


const RotatedStage = ({
  jobData,
  croppedImageRef,
  rotatedImageRef,
  gridStructure,
  selectedCells,
  cellSizeAdjustment, // Changed from cellSizeFraction
  onCellSelect,
  onCellUpdate,
  onCellSizeAdjustmentChange, // Changed from onCellSizeFractionChange
  onStatusUpdate,
  onImageLoaded,
  isLoaded,
  skipProcessing,
  additionalRotation,
  lastAdditionalRotationRef// New prop for additional rotation
}) => {
  const [rotatedImage, setRotatedImage] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [imageScale, setImageScale] = useState(1);
  const processedRef = useRef(''); // 'backend'/'frontend'/''


  useEffect(() => {
    // If backend image is available and we haven't processed yet, OR additional rotation changed
    const shouldUseBackend = rotatedImageRef.current &&
                           (!processedRef.current || processedRef.current === 'backend') &&
                           additionalRotation === 0;

    const shouldRegenerate = (!processedRef.current || processedRef.current === 'backend') &&
                           additionalRotation !== 0 &&
                           additionalRotation !== lastAdditionalRotationRef.current;
    console.log("Last additional rotation " + lastAdditionalRotationRef.current);
    console.log("Additional rotation " + additionalRotation);
    if (shouldUseBackend) {
      console.log("Rotated from backend");
      processedRef.current = 'backend';
      lastAdditionalRotationRef.current = additionalRotation;
      const image = rotatedImageRef.current;
      setRotatedImage(image);
      calculateStageSize(image);
      onImageLoaded();

      const baseRotationAngle = jobData.rotation_angle || 0;
      const totalRotationAngle = baseRotationAngle;
      const rotationText = `${(totalRotationAngle * 180 / Math.PI).toFixed(1)}°`;
      onStatusUpdate(`Rotated image: W${image.width}xH${image.height}, angle: ${rotationText}`);
    }
    // If we need to regenerate due to additional rotation change
    else if (shouldRegenerate ||
             (!processedRef.current || processedRef.current === 'frontend') &&
             croppedImageRef.current && jobData?.rotation_angle !== undefined && !skipProcessing &&
             additionalRotation !== lastAdditionalRotationRef.current) {
      console.log("Create rotated image");
      
      processedRef.current = 'frontend';
      lastAdditionalRotationRef.current = additionalRotation;
      createRotatedImage();
    }
    else {
      const image = rotatedImageRef.current;
      setRotatedImage(image);
      calculateStageSize(image);
      onImageLoaded();
      processedRef.current = 'frontend';
      lastAdditionalRotationRef.current = additionalRotation;
      const baseRotationAngle = jobData.rotation_angle || 0;
      const totalRotationAngle = baseRotationAngle + (additionalRotation * Math.PI / 180);
      const rotationText = `${(totalRotationAngle * 180 / Math.PI).toFixed(1)}°`;
      onStatusUpdate(`Rotated image: W${image.width}xH${image.height}, angle: ${rotationText}`);
    }
  }, [croppedImageRef.current, jobData, skipProcessing, additionalRotation]);

  const createRotatedImage = () => {
    const croppedImage = croppedImageRef.current;
    const baseRotationAngle = jobData.rotation_angle;
    const totalRotationAngle = baseRotationAngle + (additionalRotation * Math.PI / 180);

    if (!croppedImage) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const cos = Math.abs(Math.cos(totalRotationAngle));
    const sin = Math.abs(Math.sin(totalRotationAngle));

    let width = croppedImage.width;
    let height = croppedImage.height;

    const newWidth = width * cos + height * sin;
    const newHeight = width * sin + height * cos;

    canvas.width = newWidth;
    canvas.height = newHeight;

    ctx.translate(newWidth / 2, newHeight / 2);
    ctx.rotate(totalRotationAngle);
    ctx.drawImage(croppedImage, -width / 2, -height / 2, width, height);

    const img = new Image();
    img.onload = () => {
      setRotatedImage(img);
      rotatedImageRef.current = img;
      calculateStageSize(img);
      onImageLoaded();
      const rotationText = `${(totalRotationAngle * 180 / Math.PI).toFixed(1)}°`;
      onStatusUpdate(`Rotated image: W${img.width}xH${img.height}, angle: ${rotationText}`);
    };
    img.src = canvas.toDataURL();
  };

  const calculateStageSize = (img) => {
    const maxWidth = 800;
    const maxHeight = 600;
    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

    setStageSize({
      width: img.width * scale,
      height: img.height * scale
    });
    setImageScale(scale);
  };

  const handleCellDragEnd = (cellId, newPos) => {
    // Convert stage coordinates back to normalized coordinates
    const normalizedX = newPos.x / stageSize.width;
    const normalizedY = newPos.y / stageSize.height;

    onCellUpdate(cellId, {
      x: normalizedX,
      y: normalizedY
    });
  };

  const handleCellResize = (cellId, newSize, newPos) => {
    // Convert both size and position to normalized coordinates
    const normalizedX = newPos.x / stageSize.width;
    const normalizedY = newPos.y / stageSize.height;
    const normalizedWidth = newSize.width / stageSize.width;
    const normalizedHeight = newSize.height / stageSize.height;

    onCellUpdate(cellId, {
      x: normalizedX,
      y: normalizedY,
      width: normalizedWidth,
      height: normalizedHeight
    });
  };

  const handleCellClick = (cellId) => {
    const isSelected = selectedCells.includes(cellId);
    onCellSelect(cellId, !isSelected);
  };

  if (skipProcessing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center text-muted">
          <i className="bi bi-hourglass-split fs-1"></i>
          <div className="mt-2">Waiting for cropped image...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rotated-stage">
      {/* Stage Canvas */}
      <Stage width={stageSize.width} height={stageSize.height}>
        <Layer>
          {rotatedImage && (
            <KonvaImage
              image={rotatedImage}
              width={stageSize.width}
              height={stageSize.height}
            />
          )}
        </Layer>

        <Layer>
          {gridStructure.map((row) =>
                row.map((cell) => {
                  // Only render cells that have bbox
                  if (!cell.bbox) return null;

                  return (
                    <DraggableCell
                      key={cell.id}
                      cell={{
                        ...cell,
                        // Convert normalized coordinates to stage coordinates
                        x: cell.bbox.x * stageSize.width,
                        y: cell.bbox.y * stageSize.height,
                        width: cell.bbox.width * stageSize.width,
                        height: cell.bbox.height * stageSize.height
                      }}
                      isSelected={selectedCells.includes(cell.id)}
                      onDragEnd={(newPos) => handleCellDragEnd(cell.id, newPos)}
                      onResize={(newSize, newPos) => handleCellResize(cell.id, newSize, newPos)}
                      onClick={() => handleCellClick(cell.id)}
                      onHover={(info) => onStatusUpdate(info)}
                      stageSize={stageSize}
                    />
                  );
                })
          )}
        </Layer>
      </Stage>
    </div>
  );
};

export default RotatedStage;
